import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * Component that normalizes URLs by converting URL-encoded spaces to hyphens
 * This handles legacy URLs like /en/rent-car/fquih%20ben%20salah and redirects
 * them to the proper format /en/rent-car/fquih-ben-salah
 */
const UrlNormalizer: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const currentPath = location.pathname

    // Strip trailing slashes (except for root /) to match canonical URLs
    // Canonicals use no trailing slash; redirect /en/ -> /en to avoid "Alternate page" GSC errors
    if (currentPath.length > 1 && currentPath.endsWith('/')) {
      const normalizedPath = currentPath.slice(0, -1)
      navigate(normalizedPath + location.search + location.hash, { replace: true })
      return
    }
    
    // Check if the URL contains URL-encoded characters (like %20 for spaces)
    if (currentPath.includes('%')) {
      // Decode the URL
      const decodedPath = decodeURIComponent(currentPath)
      
      // Convert spaces to hyphens and normalize
      const normalizedPath = decodedPath
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/[^a-zA-Z0-9\u0600-\u06FF\/\-_]/g, '-') // Replace special chars with hyphens (preserve Arabic)
        .replace(/-+/g, '-') // Remove multiple consecutive hyphens
        .replace(/\/-/g, '/') // Remove hyphens after slashes
        .replace(/-\//g, '/') // Remove hyphens before slashes
      
      // Only redirect if the normalized path is different from the current path
      if (normalizedPath !== currentPath) {
        console.log('UrlNormalizer: Redirecting from', currentPath, 'to', normalizedPath)
        navigate(normalizedPath + location.search + location.hash, { replace: true })
      }
    }
  }, [location.pathname, location.search, location.hash, navigate])

  // This component doesn't render anything
  return null
}

export default UrlNormalizer
